import airtableService from './airtable';
import { Candidate } from '../types';

export interface CandidateAuthResponse {
  success: boolean;
  candidate?: Candidate;
  error?: string;
}

class CandidateAuthService {

  // Authenticate candidate with email and passcode
  async authenticateCandidate(email: string, passcode: string): Promise<CandidateAuthResponse> {
    try {
      const candidatesResponse = await airtableService.getCandidates();

      if (!candidatesResponse.success) {
        return { success: false, error: 'Failed to fetch candidate data' };
      }

      // Find candidate by email
      const candidate = candidatesResponse.data.find(c =>
        c.email && c.email.toLowerCase() === email.toLowerCase()
      );

      if (!candidate) {
        return { success: false, error: 'Invalid credentials' };
      }

      // Check if candidate status allows access (more inclusive status check)
      const allowedStatuses = ['Active', 'In Process', 'Screening', 'New'];
      if (!allowedStatuses.includes(candidate.status)) {
        return { success: false, error: 'Account access is restricted' };
      }

      // Check access passcode
      const candidatePasscode = candidate.accessPasscode || candidate.accessCode;
      if (candidatePasscode !== passcode) {
        return { success: false, error: 'Invalid credentials' };
      }

      return { success: true, candidate };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  }

  // Get candidate by name slug (firstname+lastname)
  async getCandidateByNameSlug(nameSlug: string): Promise<CandidateAuthResponse> {
    try {
      const candidatesResponse = await airtableService.getCandidates();

      if (!candidatesResponse.success) {
        return { success: false, error: 'Failed to fetch candidate data' };
      }

      // Find candidate by concatenated name (case insensitive)
      const candidate = candidatesResponse.data.find(c => {
        if (!c.firstName || !c.lastName) return false;
        const candidateSlug = `${c.firstName}${c.lastName}`.toLowerCase().replace(/\s+/g, '');
        return candidateSlug === nameSlug.toLowerCase().replace(/\s+/g, '');
      });

      if (!candidate) {
        return { success: false, error: 'Candidate not found' };
      }

      return { success: true, candidate };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch candidate'
      };
    }
  }

  // Generate name slug from candidate
  generateNameSlug(firstName: string, lastName: string): string {
    if (!firstName || !lastName) return '';
    return `${firstName}${lastName}`.toLowerCase().replace(/\s+/g, '');
  }
}

const candidateAuthService = new CandidateAuthService();
export default candidateAuthService;